
export class FormCheckboxGroup extends HTMLElement {
    static get observedAttributes() {
        return ['label', 'name', 'options', 'values'];
    }

    private _options: string[] = [];
    private _selectedValues: string[] = [];

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: any) {
        if (oldValue !== newValue) {
            if (name === 'options') {
                if (Array.isArray(newValue)) {
                    this._options = newValue;
                } else {
                    try {
                        this._options = JSON.parse(newValue);
                    } catch (e) {
                        this._options = (newValue || '').split(',').map((s: string) => s.trim()).filter(Boolean);
                    }
                }
                this.render();
            } else if (name === 'values') {
                if (Array.isArray(newValue)) {
                    this._selectedValues = newValue;
                } else {
                    try {
                        this._selectedValues = JSON.parse(newValue);
                    } catch (e) {
                        this._selectedValues = (newValue || '').split(',').map((s: string) => s.trim()).filter(Boolean);
                    }
                }
                this.render();
            } else {
                this.render();
            }
        }
    }

    get options() {
        return this._options;
    }

    set options(val: string[]) {
        this._options = val;
        this.render();
    }

    get values() {
        return this._selectedValues;
    }

    set values(val: string[]) {
        this._selectedValues = val;
        this.render();
    }

    get selectedValues() {
        return this.values;
    }

    set selectedValues(val: string[]) {
        this.values = val;
    }

    private handleCheckboxChange(e: Event) {
        const target = e.target as HTMLInputElement;
        const value = target.value;

        if (target.checked) {
            if (!this._selectedValues.includes(value)) {
                this._selectedValues.push(value);
            }
        } else {
            this._selectedValues = this._selectedValues.filter(v => v !== value);
        }

        this.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            composed: true,
            detail: { values: this._selectedValues, name: this.getAttribute('name') }
        }));

        // Update visual state locally to feel responsive (though full re-render is safe too)
        // this.render();
        // Since we re-render on attribute change, we might want to manually update selected attrib? 
        // Or just let the user update properties?
        // For self-containment, let's update attribute if it was set
        // if (this.hasAttribute('values')) {
        //    this.setAttribute('values', JSON.stringify(this._selectedValues));
        // }
    }

    render() {
        const label = this.getAttribute('label') || '';
        const name = this.getAttribute('name') || 'options';

        if (this.shadowRoot) {
            this.shadowRoot.innerHTML = `
                <style>
                    :host {
                        display: block;
                        margin-bottom: 1.5rem;
                        font-family: system-ui, -apple-system, sans-serif;
                    }
                    .form-group {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                    }
                    .header-row {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 0.25rem;
                    }
                    label.group-label {
                        color: var(--accent, #f59e0b);
                        font-size: 0.7rem;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.15em;
                        opacity: 0.8;
                    }
                    .select-toggle {
                        font-size: 0.75rem;
                        color: var(--accent, #f59e0b);
                        cursor: pointer;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                        transition: opacity 0.2s;
                        background: none;
                        border: none;
                        padding: 0;
                    }
                    .select-toggle:hover {
                        opacity: 0.8;
                        text-decoration: underline;
                    }
                    .checkbox-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
                        gap: 0.5rem;
                    }
                    .checkbox-option {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        background: rgba(255, 255, 255, 0.05);
                        padding: 0.5rem 0.75rem;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 0.85rem;
                        font-weight: 500;
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        transition: all 0.2s;
                        user-select: none;
                        color: #94a3b8;
                    }
                    .checkbox-option:hover {
                        background: rgba(255, 255, 255, 0.08);
                        border-color: rgba(255, 255, 255, 0.2);
                        color: white;
                    }
                    .checkbox-option:has(input:checked) {
                        background: rgba(245, 158, 11, 0.1);
                        border-color: rgba(245, 158, 11, 0.4);
                        color: #f59e0b;
                    }
                    .checkbox-option input {
                        margin: 0;
                        cursor: pointer;
                        accent-color: var(--accent, #f59e0b);
                    }
                </style>
                <div class="form-group">
                    <div class="header-row">
                        ${label ? `<label class="group-label">${label}</label>` : '<span></span>'}
                        <button class="select-toggle" id="toggle-all">
                            ${this._selectedValues.length === this._options.length ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>
                    <div class="checkbox-grid">
                        ${this._options.map(opt => `
                            <label class="checkbox-option">
                                <input type="checkbox" name="${name}" value="${opt}" 
                                    ${this._selectedValues.includes(opt) ? 'checked' : ''} />
                                ${opt}
                            </label>
                        `).join('')}
                    </div>
                </div>
            `;

            this.shadowRoot.querySelectorAll('input[type="checkbox"]').forEach(input => {
                input.addEventListener('change', this.handleCheckboxChange.bind(this));
            });

            this.shadowRoot.getElementById('toggle-all')?.addEventListener('click', () => {
                if (this._selectedValues.length === this._options.length) {
                    this._selectedValues = [];
                } else {
                    this._selectedValues = [...this._options];
                }
                this.render();
                this.dispatchEvent(new CustomEvent('change', {
                    bubbles: true,
                    composed: true,
                    detail: { values: this._selectedValues, name: this.getAttribute('name') }
                }));
            });
        }
    }
}

customElements.define('form-checkbox-group', FormCheckboxGroup);
